/**
 * Launch credential hygiene.
 *
 * Portal-issued launch URLs carry bearer material in the query string
 * (`sessionToken`, the QR `handoffToken`, legacy `token`). Once initialization
 * has persisted the session's refresh-continuity copy, the credentials are
 * removed from the address bar so they are not repeated in history, copied to
 * another tab, or exposed to later requests and logs.
 *
 * Readers that previously inspected `window.location.search` for these
 * parameters use `getLaunchCredential`, which returns exactly what the address
 * bar would still carry had it not been stripped: the live URL value first (a
 * fresh launch in a reused tab always wins), otherwise the value captured from
 * this tab's launch URL while that URL's `sessionId` is still present.
 */
import { sessionStateManager } from './SessionStateManager';

export const LAUNCH_CREDENTIAL_PARAMS = ['sessionToken', 'handoffToken', 'token'] as const;
export type LaunchCredentialParam = (typeof LAUNCH_CREDENTIAL_PARAMS)[number];

export const LAUNCH_CREDENTIALS_STORAGE_KEY = 'safepassage_launch_credentials';

interface CapturedLaunchCredentials {
  sessionId: string;
  params: Partial<Record<LaunchCredentialParam, string>>;
}

const isCredentialParam = (key: string): key is LaunchCredentialParam =>
  (LAUNCH_CREDENTIAL_PARAMS as readonly string[]).includes(key);

function readCapturedLaunchCredentials(): CapturedLaunchCredentials | null {
  try {
    const raw = sessionStorage.getItem(LAUNCH_CREDENTIALS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CapturedLaunchCredentials> | null;
    if (
      !parsed ||
      typeof parsed.sessionId !== 'string' ||
      !parsed.params ||
      typeof parsed.params !== 'object'
    ) {
      return null;
    }
    return parsed as CapturedLaunchCredentials;
  } catch {
    return null;
  }
}

/**
 * Returns a launch credential as the launch URL supplied it.
 */
export function getLaunchCredential(name: LaunchCredentialParam): string | null {
  const params = new URLSearchParams(window.location.search);
  const live = params.get(name);
  if (live !== null) return live;

  // A URL that still carries any credential is a launch in its own right;
  // never pair it with a value captured from an earlier launch in this tab.
  if (LAUNCH_CREDENTIAL_PARAMS.some((key) => params.has(key))) return null;

  const sessionId = params.get('sessionId');
  if (!sessionId) return null;

  const captured = readCapturedLaunchCredentials();
  if (!captured || captured.sessionId !== sessionId) return null;
  const value = captured.params[name];
  return typeof value === 'string' ? value : null;
}

export const hasLaunchCredential = (name: LaunchCredentialParam): boolean =>
  getLaunchCredential(name) !== null;

function decodeQueryKey(rawKey: string): string {
  try {
    return decodeURIComponent(rawKey.replace(/\+/g, ' '));
  } catch {
    return rawKey;
  }
}

/**
 * Removes launch credentials from the address bar once the authenticated
 * session's refresh-continuity copy is confirmed in session storage. Every
 * other query parameter (with its original encoding), the path, the hash and
 * the history state are preserved. Returns true only when the URL changed.
 *
 * It never removes the only copy: if the session copy cannot be confirmed or
 * the launch values cannot be stored, the URL is left untouched.
 */
export function removeLaunchCredentialsFromAddressBar(captured: {
  sessionId?: string;
  sessionToken?: string;
}): boolean {
  try {
    const { pathname, search, hash } = window.location;
    const segments = search.replace(/^\?/, '').split('&').filter(Boolean);
    const credentialSegments = segments.filter((segment) =>
      isCredentialParam(decodeQueryKey(segment.split('=')[0]))
    );
    if (credentialSegments.length === 0) return false;

    const params = new URLSearchParams(search);
    const sessionId = params.get('sessionId');
    if (
      !captured.sessionId ||
      !captured.sessionToken ||
      sessionId !== captured.sessionId ||
      !sessionStateManager.hasPersistedSessionCredential(
        captured.sessionId,
        captured.sessionToken
      )
    ) {
      return false;
    }

    const launchValues: CapturedLaunchCredentials = { sessionId, params: {} };
    for (const key of LAUNCH_CREDENTIAL_PARAMS) {
      const value = params.get(key);
      if (value !== null) launchValues.params[key] = value;
    }
    const serialized = JSON.stringify(launchValues);
    sessionStorage.setItem(LAUNCH_CREDENTIALS_STORAGE_KEY, serialized);
    if (sessionStorage.getItem(LAUNCH_CREDENTIALS_STORAGE_KEY) !== serialized) {
      return false;
    }

    const kept = segments.filter((segment) => !credentialSegments.includes(segment));
    const nextUrl = `${pathname}${kept.length > 0 ? `?${kept.join('&')}` : ''}${hash}`;
    window.history.replaceState(window.history.state, '', nextUrl);
    return true;
  } catch {
    return false;
  }
}

/** After Portal confirms a terminal-only binding, preserve just that original
 * binding for reload and remove it from the address bar. No active session state
 * or fresh credential is created. Storage failure keeps the original link usable. */
export function captureTerminalLaunchBinding(sessionId: string): void {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('sessionId') !== sessionId) return;
    const values: CapturedLaunchCredentials = { sessionId, params: {} };
    for (const key of LAUNCH_CREDENTIAL_PARAMS) {
      const value = getLaunchCredential(key);
      if (value !== null) values.params[key] = value;
    }
    if (!Object.keys(values.params).length) return;
    const serialized = JSON.stringify(values);
    sessionStorage.setItem(LAUNCH_CREDENTIALS_STORAGE_KEY, serialized);
    if (sessionStorage.getItem(LAUNCH_CREDENTIALS_STORAGE_KEY) !== serialized) return;
    for (const key of LAUNCH_CREDENTIAL_PARAMS) params.delete(key);
    const query = params.toString();
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${query ? '?' + query : ''}${window.location.hash}`);
  } catch { /* Keep the original link if continuity storage is unavailable. */ }
}

/** Navigation continuity survives Router dropping search and active-state TTL.
 * A fresh launch always wins; an unrelated captured tab binding is never paired
 * with a new session ID. This is deliberately not used by active operations. */
export function getTerminalLaunchBinding(): CapturedLaunchCredentials | null {
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get('sessionId');
  if (LAUNCH_CREDENTIAL_PARAMS.some(key => params.has(key))) {
    if (!sessionId) return null;
    const binding: CapturedLaunchCredentials = { sessionId, params: {} };
    for (const key of LAUNCH_CREDENTIAL_PARAMS) {
      const value = params.get(key);
      if (value !== null) binding.params[key] = value;
    }
    return binding;
  }
  const captured = readCapturedLaunchCredentials();
  return captured && (!sessionId || captured.sessionId === sessionId) ? captured : null;
}
